import React, { useCallback, useState } from 'react';
import { Button, ButtonProps, Form } from 'react-bootstrap';
import { SubmitHandler, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { faPlug } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { zodResolver } from '@hookform/resolvers/zod';
import { emitCameraThresholdEvents } from 'src/services/cameraThresholdEvents';
import {
  APP_EVENTS,
  CAMERA_CONNECTION_METHOD,
  CAMERA_CONNECTION_OUTCOME,
  captureAppEvent,
  secondsSince,
} from 'src/services/posthogEvents';
import * as z from 'zod';

import useLocalStore from 'src/store/useLocalStore';
import useRemoteBoundStore from 'src/store/useRemoteBoundStore';

import {
  CustomModal,
  CustomModalBody,
  CustomModalFooter,
  CustomModalHeader,
  CustomModalTitle,
} from 'src/components/custom_components/CustomModal';

import CameraConnection from 'src/models/Camera/CameraConnection';
import { I18NextNS } from 'src/models/Language.model';

import { captureError } from 'src/utils/errorReporting';

export type ManualConnectCameraProps = {
  isScanning: boolean;
} & ButtonProps;

export default function ManualConnectCamera({
  isScanning,
  ...props
}: ManualConnectCameraProps) {
  const { t } = useTranslation(I18NextNS.PAGES);

  const schema = z.object({
    ip: z.string().ip({
      message: t('settings.cameraModes.cameraConnection.invalidIpAddress'),
    }),
  });

  const { setCameraData } = useRemoteBoundStore();
  const { setCameraIP } = useLocalStore();

  const [isOpenInputIPModal, setIsOpenInputIPModal] = useState(false);
  const [cameraNotFoundMessage, setCameraNotFoundMessage] = useState('');

  const handleCloseInputIPModal = () => {
    setIsOpenInputIPModal(false);
    reset();
    setCameraNotFoundMessage('');
  };

  const {
    handleSubmit,
    register,
    reset,
    formState: { errors, isSubmitting },
  } = useForm({
    defaultValues: {
      ip: '',
    },
    resolver: zodResolver(schema),
  });

  const handleConnectCamera = useCallback(
    async (ip: string) => {
      const connectStart = performance.now();
      try {
        const cameraData = await CameraConnection.loadCameraData(
          ip,
          t('message.error.cameraIpMissingMsg', { ns: I18NextNS.COMMON }),
        );

        await CameraConnection.getInstance().say(
          'Camera successfully connected.',
        );

        setCameraData(cameraData);
        emitCameraThresholdEvents(cameraData);
        setCameraIP(ip);
        captureAppEvent(APP_EVENTS.CAMERA_CONNECTION_ATTEMPTED, {
          method: CAMERA_CONNECTION_METHOD.MANUAL_IP,
          outcome: CAMERA_CONNECTION_OUTCOME.SUCCESS,
          duration_seconds: secondsSince(connectStart),
          camera_serial_number: cameraData.deviceSN,
          camera_version: cameraData.versionInfo?.version,
          camera_side_gain: cameraData.sideGain.data,
          camera_main_gain: cameraData.mainGain.data,
        });
      } catch (error) {
        captureAppEvent(APP_EVENTS.CAMERA_CONNECTION_ATTEMPTED, {
          method: CAMERA_CONNECTION_METHOD.MANUAL_IP,
          outcome: CAMERA_CONNECTION_OUTCOME.FAILURE,
          duration_seconds: secondsSince(connectStart),
        });
        captureError(error, 'Camera has not been found');

        setCameraNotFoundMessage(
          t('message.error.cameraNotFound', { ns: I18NextNS.COMMON }),
        );
      }
    },
    [setCameraData, t, setCameraIP],
  );

  const onSubmit: SubmitHandler<{
    ip: string;
  }> = async (data) => {
    await handleConnectCamera(data.ip);
  };

  return (
    <>
      <Button
        variant="dark"
        onClick={() => setIsOpenInputIPModal(true)}
        disabled={isScanning}
        {...props}
        data-track="camera.manual-connect.open-modal"
        data-observe
      >
        <FontAwesomeIcon
          icon={faPlug}
          className="me-2"
          data-test="settings-manual-connect-button"
        />
        {t('settings.cameraModes.cameraConnection.manualConnect')}
      </Button>

      <CustomModal
        closeButton
        show={isOpenInputIPModal}
        onHide={handleCloseInputIPModal}
      >
        <CustomModalHeader>
          <CustomModalTitle data-test="settings-connect-device-modal-title">
            {t('settings.cameraModes.cameraConnection.connectToDevice')}
          </CustomModalTitle>
        </CustomModalHeader>
        <Form onSubmit={handleSubmit(onSubmit)}>
          <CustomModalBody>
            <Form.Group className="mb-3" controlId="ip">
              <Form.Label data-test="settings-connect-device-ip-label">
                {t('settings.cameraModes.cameraConnection.ipAddress')}:
              </Form.Label>
              <Form.Control
                type="text"
                placeholder={t(
                  'settings.cameraModes.cameraConnection.ipAddressPlaceholder',
                )}
                data-test="settings-connect-device-ip-input"
                {...register('ip')}
                onInput={() => setCameraNotFoundMessage('')}
              />
              {cameraNotFoundMessage && (
                <p
                  className="text-danger"
                  data-test="settings-connect-device-error"
                >
                  {cameraNotFoundMessage}
                </p>
              )}
              {errors.ip?.message && (
                <p
                  className="text-danger"
                  data-test="settings-connect-device-error"
                >
                  {errors.ip?.message}
                </p>
              )}
            </Form.Group>
          </CustomModalBody>
          <CustomModalFooter>
            <Button
              variant="primary-100"
              onClick={handleCloseInputIPModal}
              data-test="settings-connect-device-close-button"
              data-track="camera.manual-connect-modal.close"
              data-observe
            >
              {t('settings.cameraModes.cameraConnection.close')}
            </Button>
            <Button
              variant="primary"
              type="submit"
              disabled={isSubmitting}
              onClick={() => setCameraNotFoundMessage('')}
              data-test="connect-camera"
              data-track="camera.manual-connect-modal.connect"
              data-observe
            >
              {isSubmitting
                ? `${t('settings.cameraModes.cameraConnection.connecting')}...`
                : `${t('settings.cameraModes.cameraConnection.connect')}`}
            </Button>
          </CustomModalFooter>
        </Form>
      </CustomModal>
    </>
  );
}
